import { Component, Input, OnInit } from '@angular/core';
import {
  ADJUSTMENT_46_LIST_KEY,
  ADJUSTMENT_46_TYPE,
  BUSINESS_ADDRESS_KEY,
  BUSINESS_CATEGORY_KEY,
  BUSINESS_CATEGORY_LABELS,
  BUSINESS_NAME_KEY,
  BUSINESS_TYPE_KEY,
  BUSINESS_TYPE_LABELS,
  COMMON_PARTICULARS_KEY,
  COMMON_PARTICULARS_LABELS,
  FAILURE_OF_DEDUCTION_COLUMNS,
  FAILURE_OF_DEDUCTION_LIST_KEY,
  FAILURE_OF_DEDUCTION_TYPE,
  INCOME_FORM_BUSINESS_KEYS,
  IncomeFormBusinessKey,
  SPECIAL_BUSINESS_ADJUSTMENT_OPTIONS,
} from '../../../../constants/income-from-business-constant';

@Component({
    selector: 'app-income-from-business-en-v2',
    templateUrl: './income-from-business-en-v2.component.html',
    styleUrls: [
        './income-from-business-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class IncomeFromBusinessEnV2Component implements OnInit {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  incomeFormBusinessData: any[] = [];

  readonly royaltyCategoryValue = 'BUS_INCOME_FROM_ROYALTY_INTANGIBLES';

  readonly businessCategoryKey = BUSINESS_CATEGORY_KEY;
  readonly businessTypeKey = BUSINESS_TYPE_KEY;
  readonly businessNameKey = BUSINESS_NAME_KEY;
  readonly businessAddressKey = BUSINESS_ADDRESS_KEY;
  readonly commonParticularsKey = COMMON_PARTICULARS_KEY;
  readonly failureOfDeductionColumns = FAILURE_OF_DEDUCTION_COLUMNS;
  readonly maintainBooksKey = 'maintainBooksOfAccounts';

  constructor() { }

  getValue(item: any, key: string): any {
    return item ? item[key] : null;
  }

  getBusinessCategoryTitle(category: any): string {
    const v = category === null || category === undefined ? '' : String(category).trim();
    return BUSINESS_CATEGORY_LABELS[v] || v;
  }

  getBusinessTypeTitle(businessType: any): string {
    const v = businessType === null || businessType === undefined ? '' : String(businessType).trim();
    return BUSINESS_TYPE_LABELS[v] || v;
  }

  getCommonParticularsTitle(particular: any): string {
    const v = particular === null || particular === undefined ? '' : String(particular).trim();
    return COMMON_PARTICULARS_LABELS[v] || v;
  }

  isSpecialBusinessIncome(item: any): boolean {
    return String(this.getValue(item, this.businessCategoryKey) || '').trim() === 'SPECIAL_BUS_INCOME';
  }

  getAdjustmentParticularTitle(particular: any): string {
    const v = particular === null || particular === undefined ? '' : String(particular).trim();
    return SPECIAL_BUSINESS_ADJUSTMENT_OPTIONS[v] || v;
  }

  hasValue(item: any, key: string): boolean {
    const val = this.getValue(item, key);
    return val !== null && val !== undefined && val !== '';
  }

  isFailureOfDeduction(item: any): boolean {
    return this.getValue(item, this.commonParticularsKey) === FAILURE_OF_DEDUCTION_TYPE;
  }

  isAdjustment46(item: any): boolean {
    return this.getValue(item, this.commonParticularsKey) === ADJUSTMENT_46_TYPE;
  }

  isRoyaltyIntangiblesCategory(item: any): boolean {
    return String(this.getValue(item, this.businessCategoryKey) || '').trim() === this.royaltyCategoryValue;
  }

  getRoyaltyIntangibleRows(item: any): any[] {
    if (Array.isArray(item?.royaltyIntangibleRows)) {
      return item.royaltyIntangibleRows;
    }

    // Fallback: sometimes backend may not send row array; treat category-level fields as a single row.
    const hasRowLikeData =
      this.getValue(item, 'grossProfit') !== null ||
      this.getValue(item, 'otherExpenseAmount') !== null ||
      this.getValue(item, 'taxToDeducted') !== null ||
      this.getValue(item, 'tds') !== null ||
      this.getValue(item, 'paymentDate') !== null ||
      this.getValue(item, 'businessTypeName') !== null ||
      this.getValue(item, 'businessName') !== null ||
      this.getValue(item, 'businessNm') !== null;

    return hasRowLikeData ? [item] : [];
  }

  getRoyaltyBusinessItems(): any[] {
    return (this.incomeFormBusinessData ?? []).filter((item) => this.isRoyaltyIntangiblesCategory(item));
  }

  /** Flatten all royalty/intangibles rows across all matching business entries. */
  getRoyaltyTableRows(): any[] {
    return this.getRoyaltyBusinessItems().reduce((acc: any[], item) => {
      return acc.concat(this.getRoyaltyIntangibleRows(item));
    }, []);
  }

  private toNumber(v: any): number {
    if (v === null || v === undefined || v === '' || v === '0') {
      return 0;
    }
    if (typeof v === 'number') {
      return isNaN(v) ? 0 : v;
    }
    const raw = String(v).replace(/,/g, '');
    const n = Number(raw);
    return isNaN(n) ? 0 : n;
  }

  getRoyaltyRowTdsValue(row: any): any {
    // UI uses `taxToDeducted` (user-panel form). Saved payload may also carry `tds`.
    return this.getValue(row, 'taxToDeducted') ?? this.getValue(row, 'tds') ?? 0;
  }

  getRoyaltyRowNetIncomeValue(row: any): number {
    const gross = this.toNumber(this.getValue(row, 'grossProfit'));
    const expense = this.toNumber(this.getValue(row, 'otherExpenseAmount'));
    return gross - expense;
  }

  getRoyaltyTotalGrossProfit(item: any): number {
    return this.getRoyaltyIntangibleRows(item).reduce((sum, row) => {
      return sum + this.toNumber(this.getValue(row, 'grossProfit'));
    }, 0);
  }

  getRoyaltyTotalOtherExpenseAmount(item: any): number {
    return this.getRoyaltyIntangibleRows(item).reduce((sum, row) => {
      return sum + this.toNumber(this.getValue(row, 'otherExpenseAmount'));
    }, 0);
  }

  getRoyaltyNetIncome(item: any): number {
    const rows = this.getRoyaltyIntangibleRows(item);
    if (!rows.length) {
      return 0;
    }

    return rows.reduce((sum, row) => {
      const gross = this.toNumber(this.getValue(row, 'grossProfit'));
      const expense = this.toNumber(this.getValue(row, 'otherExpenseAmount'));
      return sum + (gross - expense);
    }, 0);
  }

  getRoyaltyTotalTds(item: any): number {
    const rows = this.getRoyaltyIntangibleRows(item);
    if (!rows.length) {
      return 0;
    }

    return rows.reduce((sum, row) => {
      return sum + this.toNumber(this.getRoyaltyRowTdsValue(row));
    }, 0);
  }

  getRoyaltyTableTotalGrossProfit(): number {
    return this.getRoyaltyTableRows().reduce((sum, row) => {
      return sum + this.toNumber(this.getValue(row, 'grossProfit'));
    }, 0);
  }

  getRoyaltyTableTotalOtherExpenseAmount(): number {
    return this.getRoyaltyTableRows().reduce((sum, row) => {
      return sum + this.toNumber(this.getValue(row, 'otherExpenseAmount'));
    }, 0);
  }

  getRoyaltyTableNetIncome(): number {
    return this.getRoyaltyTableRows().reduce((sum, row) => {
      return sum + this.getRoyaltyRowNetIncomeValue(row);
    }, 0);
  }

  getRoyaltyTableTotalTds(): number {
    return this.getRoyaltyTableRows().reduce((sum, row) => {
      return sum + this.toNumber(this.getRoyaltyRowTdsValue(row));
    }, 0);
  }

  maintainsBooksOfAccounts(item: any): boolean {
    const val = this.getValue(item, this.maintainBooksKey);
    return val === true || val === 1 || val === '1' || val === 'true' || val === 'Yes' || val === 'YES';
  }

  isApplicableForItem(particular: IncomeFormBusinessKey, item: any): boolean {
    const category = String(this.getValue(item, this.businessCategoryKey) || '').trim();
    const commonParticular = String(this.getValue(item, this.commonParticularsKey) || '').trim();

    if (particular.categories && particular.categories.length > 0 && !particular.categories.includes(category)) {
      return false;
    }

    if (particular.excludeCommonParticulars && particular.excludeCommonParticulars.includes(commonParticular)) {
      return false;
    }

    if (particular.commonParticulars && particular.commonParticulars.length > 0) {
      return particular.commonParticulars.includes(commonParticular);
    }

    // Match main business form: TDS fields are category + business-type dependent.
    if (particular.key === 'tds') {
      return this.shouldShowTds(item, category);
    }
    if (particular.key === 'tds120') {
      return this.shouldShowTds120(item, category);
    }

    return true;
  }

  /** Mirrors business-profession form visibility for primary TDS (`taxToDeducted` / `tds`). */
  shouldShowTds(item: any, category?: string): boolean {
    const cat = category ?? String(this.getValue(item, this.businessCategoryKey) || '').trim();
    const businessType = String(this.getValue(item, this.businessTypeKey) || '').trim();

    if (cat === 'REG_BUS_INCOME') {
      return businessType === 'MANUFACTURING_WITH_TDS';
    }

    // BSMT categories: primary TDS always shown (plain, claim, U/S 89, or U/S 123).
    return cat.startsWith('BSMT_');
  }

  /** Mirrors form: U/S 120 only for export manufacturing/trading and supply of imported items. */
  shouldShowTds120(item: any, category?: string): boolean {
    const cat = category ?? String(this.getValue(item, this.businessCategoryKey) || '').trim();
    const businessType = String(this.getValue(item, this.businessTypeKey) || '').trim();

    if (cat === 'BSMT_CONTRACT_OR_SUPPLY' && businessType === 'SUPPLY_OF_IMPORTED_ITEMS') {
      return true;
    }
    if (cat === 'BSMT_EXPORT' && (businessType === 'MANUFACTURING' || businessType === 'TRADING')) {
      return true;
    }
    return false;
  }

  getAmountParticularTitle(particular: IncomeFormBusinessKey, item: any): string {
    if (particular.key !== 'tds') {
      return particular.title;
    }

    const category = String(this.getValue(item, this.businessCategoryKey) || '').trim();
    const businessType = String(this.getValue(item, this.businessTypeKey) || '').trim();

    if (category === 'BSMT_EXPORT' && (businessType === 'MANUFACTURING' || businessType === 'TRADING')) {
      return 'Tax Deducted/Collected at Source U/S 123';
    }
    if (category === 'BSMT_CONTRACT_OR_SUPPLY' && businessType === 'SUPPLY_OF_IMPORTED_ITEMS') {
      return 'Tax Deducted/Collected at Source U/S 89';
    }
    return particular.title;
  }

  getTextParticulars(item: any): IncomeFormBusinessKey[] {
    return INCOME_FORM_BUSINESS_KEYS
      .filter((k) => {
        if (k.type !== 'text' || k.key === this.businessCategoryKey || !this.isApplicableForItem(k, item) || !this.hasValue(item, k.key)) {
          return false;
        }
        // Avoid showing "Select"/placeholder income type.
        if (k.key === this.commonParticularsKey && String(this.getValue(item, k.key)).trim() === '0') {
          return false;
        }
        return true;
      })
      .slice()
      .sort((a, b) => a.order - b.order);
  }

  getTextFieldDisplayValue(item: any, field: IncomeFormBusinessKey): string {
    const value = this.getValue(item, field.key);
    if (field.key === this.businessCategoryKey) {
      return this.getBusinessCategoryTitle(value);
    }
    if (field.key === this.businessTypeKey) {
      return this.getBusinessTypeTitle(value);
    }
    if (field.key === this.commonParticularsKey) {
      return this.getCommonParticularsTitle(value);
    }
    if (field.key === this.maintainBooksKey) {
      return this.formatYesNo(value);
    }
    if (field.key === 'teaRubberCultivationArea') {
      const unit = this.getValue(item, 'unitOfTotalCulArea');
      return unit ? `${value} (${unit})` : String(value);
    }
    if (field.key === 'unitOfTotalCulArea') {
      return '';
    }
    return String(value ?? '');
  }

  shouldShowTextField(field: IncomeFormBusinessKey): boolean {
    return field.key !== 'unitOfTotalCulArea' && field.key !== this.businessCategoryKey;
  }

  private formatYesNo(value: any): string {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 'Yes' || value === 'YES') {
      return 'Yes';
    }
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'No' || value === 'NO') {
      return 'No';
    }
    return String(value ?? '');
  }

  private getAmountKeysByGroup(item: any, group?: 'expenses' | 'balanceSheet'): IncomeFormBusinessKey[] {
    if (this.isFailureOfDeduction(item) || this.isAdjustment46(item)) {
      return [];
    }

    return INCOME_FORM_BUSINESS_KEYS
      .filter((k) =>
        k.type === 'amount'
        && this.isApplicableForItem(k, item)
        && k.group === group
        && this.hasValue(item, k.key)
      )
      .slice()
      .sort((a, b) => a.order - b.order);
  }

  getAmountParticulars(item: any): IncomeFormBusinessKey[] {
    return this.getAmountKeysByGroup(item, undefined);
  }

  getExpenseParticulars(item: any): IncomeFormBusinessKey[] {
    return this.getAmountKeysByGroup(item, 'expenses');
  }

  getBalanceSheetParticulars(item: any): IncomeFormBusinessKey[] {
    if (!this.maintainsBooksOfAccounts(item)) {
      return [];
    }
    return this.getAmountKeysByGroup(item, 'balanceSheet');
  }

  getFailureOfDeductionList(item: any): any[] {
    const list = item?.[FAILURE_OF_DEDUCTION_LIST_KEY] ?? item?.specialBusinessFailureOfPaymentList;
    return Array.isArray(list) ? list : [];
  }

  getFailureOfDeductionRowValue(row: any, key: string): any {
    return row ? row[key] : null;
  }

  getFailureOfDeductionTotal(item: any, field: string): number {
    return this.getFailureOfDeductionList(item).reduce((total, row) => {
      const value = Number(this.getFailureOfDeductionRowValue(row, field));
      return total + (isNaN(value) ? 0 : value);
    }, 0);
  }

  getAdjustment46List(item: any): any[] {
    const list = item?.[ADJUSTMENT_46_LIST_KEY];
    return Array.isArray(list) ? list : [];
  }

  getAdjustment46RowValue(row: any, key: string): any {
    return row ? row[key] : null;
  }

  getSpecialBusinessIncome(item: any): any {
    return this.getValue(item, 'specialBusinessIncome');
  }

  ngOnInit(): void {
    const businessData = this.returnData?.incomeData?.businessData?.data;
    this.incomeFormBusinessData = Array.isArray(businessData) ? businessData : [];
  }
}
